import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { Stack, Card, Typography, useMediaQuery } from '@mui/material';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { Link } from 'react-router-dom';

import { usePoliciesQuery } from '../../../../api/getPolicies';
import AutoPayIcon from '../../../../assets/icons/AutoPay';
import CircularWarning from '../../../../assets/icons/CircularWarning';
import CardHeader from '../../../../components/CardHeader';
import Button from '../../../../components/styledComponents/Button';
import StyledLink from '../../../../components/styledComponents/Link';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../store/SnackbarProvider';
import { usePaymentsContext } from '../../../../store/payments';
import {
  addSelectedBill,
  resetPaymentState,
} from '../../../../store/paymentsReducer';
import theme from '../../../../styles/theme';
import {
  policyTypeMap,
  policyTypeMapSelector,
} from '../../../../utils/policyTypeUtil';

interface BillCardProps {
  bill: BillingDetails;
}

const BillCard = ({ bill }: BillCardProps) => {
  const {
    policyNumber,
    policyTypeCd,
    lastPaymentAmount,
    minimumDueAmount,
    autoPay,
    pastDue,
    lastPaymentDate,
    totalDueAmount,
    mortgageeBilled,
    riskState,
    fullProductCode,
  } = bill;
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const { data: policiesData } = usePoliciesQuery();
  const policy = policiesData?.policies?.find(
    (policy) => policy.policyNumber === policyNumber
  );
  const policyDetails = policy?.policyDetails;
  const { dispatch } = usePaymentsContext();
  const { track } = useAnalytics();

  const showMakePaymentButton =
    parseFloat(minimumDueAmount) > 0 || parseFloat(totalDueAmount) > 0;
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const isZeroDue = parseFloat(minimumDueAmount) <= 0;
  const isOverpaid = parseFloat(totalDueAmount) < 0;
  const paidInFull =
    parseFloat(minimumDueAmount) === 0 && parseFloat(totalDueAmount) <= 0;

  const showBalanceSubText = !paidInFull && !isOverpaid;

  const policyCancelled = policy?.policyStatus.toLowerCase().includes('cancel');
  const policyExpired = policy?.policyStatus === 'Expired';
  const policyTitleIndex = policyTypeMapSelector(
    bill.policyTypeCd,
    bill.fullProductCode
  );
  const policyTitle = policyTypeMap[policyTitleIndex].title;

  const getDueDate = () => {
    if (paidInFull) {
      return 'Paid in full';
    }
    return isZeroDue ? '' : `Minimum due ${bill.dueDate}`;
  };

  useEffect(() => {
    if (autoPay.card?.expired) {
      enqueueSnackbar({
        dataTestId: 'expired-alert',
        title: 'AutoPay payment method has expired',
        message: 'Please update',
        variant: 'info',
        key: 'expired',
        autoHideDuration: 10000,
      });
    }

    return () => {
      window.location.pathname !== '/bills' && closeSnackbar('expired');
    };
  }, [autoPay]);

  const handleAutoPayClick = () => {
    track('AutoPay Settings Accessed', {
      event_type: 'Link Accessed',
      location: 'Feature Card',
      link: 'AutoPay',
      policy_number: policyNumber,
      state: riskState,
      policy_type: policyTitle,
    });
  };

  const AutoPayMessage = () => {
    if (autoPay.card?.expired) {
      return (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <CircularWarning sx={{ height: 20, width: 20 }} />
          <Typography
            sx={{
              fontSize: { mobile: 16, tablet: 18 },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaDark,
            }}
            data-testid={`autopay-expired-text-${policyNumber}`}
          >
            AutoPay payment method has expired.{' '}
            <StyledLink
              onClick={handleAutoPayClick}
              to="/autopay"
              sx={{
                fontSize: { mobile: 16, tablet: 18 },
                fontWeight: 700,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                letterSpacing: '0.4px',
              }}
              data-testid={`autopay-expired-manage-button-${policyNumber}`}
            >
              Manage AutoPay
            </StyledLink>
          </Typography>
        </Stack>
      );
    }

    if (autoPay.enrolled && autoPay.dueDate) {
      return (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <AutoPayIcon />
          <Typography
            sx={{
              fontSize: { mobile: 16, tablet: 18 },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
              letterSpacing: '0.4px',
            }}
            data-testid={`autopay-enrolled-text-${policyNumber}`}
          >
            AutoPay is scheduled for your installment date.{' '}
            <StyledLink
              onClick={handleAutoPayClick}
              to="/autopay"
              sx={{
                fontSize: { mobile: 16, tablet: 18 },
                fontWeight: 700,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                letterSpacing: '0.4px',
              }}
              data-testid={`autopay-enrolled-manage-link-${policyNumber}`}
            >
              Manage AutoPay
            </StyledLink>
            .
          </Typography>
        </Stack>
      );
    } else if (autoPay.eligible) {
      return (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <AutoPayIcon />
          <Typography
            sx={{
              fontSize: { mobile: 16, tablet: 18 },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            Set up{' '}
            <StyledLink
              onClick={handleAutoPayClick}
              to="/autopay"
              data-testid={`autopay-setup-link-${policyNumber}`}
            >
              AutoPay
            </StyledLink>
          </Typography>
        </Stack>
      );
    }

    return null;
  };

  const handleMakePayment = () => {
    track('Payment Initiated', {
      event_type: 'Link Accessed',
      location: 'Feature Card',
      link: 'Make a payment',
      payment_type: 'One Policy',
      policy_number: policyNumber,
      state: riskState,
      policy_type: policyTitle,
    });
    dispatch(resetPaymentState());
    dispatch(
      addSelectedBill({
        ...bill,
        policyType: policyTitle,
        policyStatus: policy?.policyStatus ?? '',
      })
    );
  };

  const CardButtons = () => (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      sx={{
        alignItems: "center",
        ml: 0,
        my: { mobile: 2, tablet: 0 }
      }}>
      {showMakePaymentButton && (
        <Button
          component={Link}
          to="/payment"
          data-testid={`make-payment-button-${policyNumber}`}
          variant="contained"
          sx={{
            whiteSpace: 'nowrap',
          }}
          onClick={handleMakePayment}
        >
          Make a payment
        </Button>
      )}
    </Stack>
  );

  return (
    <Card
      component={Stack}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: { tablet: 5, mobile: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <CardHeader
        cancelled={policyCancelled}
        expired={policyExpired}
        headerText={policyNumber.toUpperCase()}
        policyTypeCd={policyTypeCd}
        vehicles={policyDetails?.vehicles}
        insuredAddress={policyDetails?.insuredAddress}
        pastDue={pastDue}
        mortgageeBilled={mortgageeBilled}
        isNanoPolicy={policy?.nanoPolicy}
        policyNumber={policyNumber}
        fullProductCode={fullProductCode}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mt: { mobile: 2, tablet: 3 }
        }}>
        <Stack direction="column">
          {!!getDueDate() && (
            <Typography
              sx={{
                fontWeight: 400,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                color: colors.csaaMid,
              }}
            >
              {getDueDate()}
            </Typography>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { desktop: 44, tablet: 36, mobile: 33 },
              lineHeight: '57px',
              color: colors.csaaBlack,
            }}
          >
            <NumericFormat
              value={bill.minimumDueAmount}
              displayType="text"
              thousandSeparator
              prefix="$"
              fixedDecimalScale
              decimalScale={2}
            />
          </Typography>
          {showBalanceSubText && (
            <Typography
              sx={{
                fontWeight: 400,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                color: colors.csaaMid,
              }}
            >
              Pay in full:{' '}
              <strong>
                <NumericFormat
                  value={bill.totalDueAmount}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                />
              </strong>
            </Typography>
          )}
        </Stack>

        {!isMobile && <CardButtons />}
      </Stack>
      {isMobile && <CardButtons />}
      <Stack
        direction="column"
        spacing={{ mobile: 1, tablet: 2 }}
        sx={{
          mt: { mobile: 1, tablet: 5 },
        }}
      >
        {lastPaymentAmount && parseFloat(lastPaymentAmount) !== 0 && (
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaDark,
            }}
          >
            Thanks for your last payment of{' '}
            <NumericFormat
              value={lastPaymentAmount}
              prefix="$"
              displayType="text"
              thousandSeparator
              fixedDecimalScale
              decimalScale={2}
            />{' '}
            on{' '}
            {lastPaymentDate && format(new Date(lastPaymentDate), 'MM/dd/yyyy')}
          </Typography>
        )}
        {(autoPay.enrolled || autoPay.eligible) && <AutoPayMessage />}
      </Stack>
    </Card>
  );
};

export default BillCard;
